/*----------------------------------
- SALES PAGE: WALLS
----------------------------------*/

/**
 * @docs docs/features/sales-page
 * @adr  ADR-0067
 * @rule Every control a visitor meets on `/sales` that members use opens the signup wall with its
 *       own wall action and trigger, and sends the new account back to the Product view it
 *       promised (`SalesAuthIntent.returnTo`). None reorders, filters or pages the rows. Each
 *       trigger carries the same action in the trigger catalog (ADR-0050).
 */

import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import { DATABASE_SEARCH_ACTIONS } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import {
    SALES_ALERT_CREATE_ACTION,
    SALES_FILTER_APPLY_ACTION,
    SALES_LADDER_SORT_ACTION,
    SALES_LANE_ACTION,
    SALES_MORE_ACTION,
} from '@uniquedomains/components/contracts/upgrade/catalogs/featureActions';
import type { WebsiteConversionWallAction } from '@uniquedomains/components/conversion/wallActions';
import type { SalesAuthIntent, SalesAuthKind } from '@uniquedomains/components/sales/salesTypes';

import { SALES_PAGE_CONTENT } from '@/client/catalogs/landing/sales/pageContent';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { buildProductPageConversionContext } from '@/client/services/conversion/intentContext';
import { buildWebsiteAuthHref, openWebsiteAuthHref } from '@/client/utils/conversion/authHref';

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

/** Signup attribution of every wall and CTA of the page (`User.desiredFrom`). */
export const SALES_ATTRIBUTION = 'website_sales';

export const SALES_TRACKING_PAGE = 'sales';

/**
 * Each kind's wall action and trigger. `plan` never reaches the website (a visitor has no plan). A
 * row opens the domain-details wall of the public listing's rows (it promises that name's venue and
 * earlier sales); a day's second lookup searches the archive for a word, so it opens the filter
 * wall.
 */
const SALES_WALLS: Record<SalesAuthKind, { action: WebsiteConversionWallAction; trigger: ConversionTriggerId }> = {
    filter: { action: SALES_FILTER_APPLY_ACTION, trigger: 'website_sales_filter' },
    plan: { action: SALES_FILTER_APPLY_ACTION, trigger: 'website_sales_filter' },
    more: { action: SALES_MORE_ACTION, trigger: 'website_sales_more' },
    row: { action: DATABASE_SEARCH_ACTIONS.unlockDomainDetails, trigger: 'website_sales_row' },
    lookup: { action: SALES_FILTER_APPLY_ACTION, trigger: 'website_sales_lookup' },
    alert: { action: SALES_ALERT_CREATE_ACTION, trigger: 'website_sales_alert' },
    near: { action: SALES_LANE_ACTION, trigger: 'website_sales_lane' },
    sold: { action: SALES_LANE_ACTION, trigger: 'website_sales_lane' },
    ladderSort: { action: SALES_LADDER_SORT_ACTION, trigger: 'website_sales_ladder_sort' },
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const buildSalesWallHref = (intent: Pick<SalesAuthIntent, 'kind' | 'returnTo'>): string =>
    buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        from: SALES_ATTRIBUTION,
        returnTo: intent.returnTo,
        action: SALES_WALLS[intent.kind].action,
        context: buildProductPageConversionContext(SALES_PAGE_CONTENT.meta.breadcrumb),
    });

/** Track the click and open the wall in place (the conversion modal). */
export const openSalesWall = (intent: SalesAuthIntent): void => {
    const href = buildSalesWallHref(intent);
    trackConversionTrigger(SALES_WALLS[intent.kind].trigger, {
        href,
        page: SALES_TRACKING_PAGE,
        section: intent.section,
        ...(intent.extra ? { context: { extra: intent.extra } } : {}),
    });
    openWebsiteAuthHref(href);
};
